/**
 * Captures mic audio as raw 16-bit PCM, mono, 16kHz, delivered via onChunk
 * roughly every 100ms. Uses an AudioWorkletNode where available, falling
 * back to the deprecated ScriptProcessorNode on browsers without it.
 */

const SAMPLE_RATE = 16000;
const CHUNK_SAMPLES = SAMPLE_RATE / 10; // ~100ms

const WORKLET_SOURCE = `
class PCMProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.buffer = [];
    this.chunkSamples = ${CHUNK_SAMPLES};
  }
  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    if (channel) {
      for (let i = 0; i < channel.length; i++) this.buffer.push(channel[i]);
      while (this.buffer.length >= this.chunkSamples) {
        const slice = this.buffer.splice(0, this.chunkSamples);
        const pcm16 = new Int16Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          const s = Math.max(-1, Math.min(1, slice[i]));
          pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }
        this.port.postMessage(pcm16.buffer, [pcm16.buffer]);
      }
    }
    return true;
  }
}
registerProcessor('pcm-processor', PCMProcessor);
`;

function floatTo16BitPCM(channel) {
  const pcm16 = new Int16Array(channel.length);
  for (let i = 0; i < channel.length; i++) {
    const s = Math.max(-1, Math.min(1, channel[i]));
    pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return pcm16.buffer;
}

/**
 * @param {(chunk: ArrayBuffer) => void} onChunk
 * @returns {Promise<{ stop: () => void }>}
 */
export async function startPcmCapture(onChunk) {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
  const audioContext = new AudioContextCtor({ sampleRate: SAMPLE_RATE });
  if (audioContext.state === 'suspended') await audioContext.resume();
  const source = audioContext.createMediaStreamSource(stream);

  // Silent sink — some browsers only process nodes reachable from destination.
  const muteGain = audioContext.createGain();
  muteGain.gain.value = 0;

  let node;
  let workletUrl = null;

  if (audioContext.audioWorklet) {
    workletUrl = URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: 'application/javascript' }));
    await audioContext.audioWorklet.addModule(workletUrl);
    node = new AudioWorkletNode(audioContext, 'pcm-processor');
    node.port.onmessage = (e) => onChunk(e.data);
  } else {
    // ScriptProcessorNode fallback — buffer size must be a power of two;
    // 2048 @ 16kHz is ~128ms, close to the 100ms target.
    node = audioContext.createScriptProcessor(2048, 1, 1);
    node.onaudioprocess = (e) => {
      onChunk(floatTo16BitPCM(e.inputBuffer.getChannelData(0)));
    };
  }

  source.connect(node);
  node.connect(muteGain);
  muteGain.connect(audioContext.destination);

  return {
    stop() {
      try { node.disconnect(); } catch { /* ignore */ }
      try { source.disconnect(); } catch { /* ignore */ }
      try { muteGain.disconnect(); } catch { /* ignore */ }
      stream.getTracks().forEach((t) => t.stop());
      audioContext.close();
      if (workletUrl) URL.revokeObjectURL(workletUrl);
    },
  };
}
